import { Sim, Month, Weekday } from '../../game/simulation';
import { ICON } from '../icons';
import { t } from '../strings';
import type { PanelManager } from '../core/PanelManager';

/**
 * Top-right consolidated info card — Stardew-style.
 *
 *   ┌───────────────────────┐
 *   │  [sun] 09:42          │  ← clock + time-of-day icon
 *   │  Wed · Mar 15         │  ← weekday + month + day-of-month
 *   │  Year 1 · [spring]    │  ← year + season
 *   ├───────────────────────┤
 *   │  [coin] 60            │  ← money chip (own row)
 *   ├───────────────────────┤
 *   │  Plot overview        │  ← only when state.activePlotIdx != null
 *   │  Tiles: 12            │
 *   │  Weeds: 3             │
 *   │  Tilled:  72% (138/192)
 *   │  Watered: 41% ( 79/192)
 *   │  Seeded:  31% ( 60/192)
 *   └───────────────────────┘
 *
 * Drives entirely off `sim.getDate()` which returns the full calendar
 * struct (year / month / day-of-month / weekday / season). Future
 * seasonal-farming mechanics will hook the same struct.
 *
 * Exposes the `data-id="money"` anchor so FlyToHud's `target('money')`
 * resolves here.
 */
const WEEKDAY_SHORT: Record<Weekday, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
  friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};
const MONTH_SHORT: Record<Month, string> = {
  january:  'Jan', february: 'Feb', march:    'Mar', april:    'Apr',
  may:      'May', june:     'Jun', july:     'Jul', august:   'Aug',
  september:'Sep', october:  'Oct', november: 'Nov', december: 'Dec',
};
const SEASON_LABEL: Record<string, string> = {
  spring: 'Spring',
  summer: 'Summer',
  fall:   'Fall',
  winter: 'Winter',
};
const SEASON_ICON: Record<string, string> = {
  spring: ICON.spring,
  summer: ICON.summer,
  fall:   ICON.fall,
  winter: ICON.winter,
};

export class InfoCard {
  private root: HTMLDivElement;
  private lastHash = '';
  private lastPlotHash = '';
  private plotEl: HTMLDivElement;
  private energyFill: HTMLDivElement;
  private energyValue: HTMLDivElement;
  private lastEnergyHash = '';
  /** Lazily-set panel manager — wired by main.ts after construction
   *  so the Shop chip can route to the Truck panel without InfoCard
   *  needing it at construction time. */
  private panels: PanelManager | null = null;

  constructor(parent: HTMLElement, private sim: Sim) {
    this.root = document.createElement('div');
    this.root.className = 'ui-infocard';
    this.root.innerHTML = `
      <div class="ui-infocard__row ui-infocard__row--clock">
        <span class="ui-infocard__sun glyph" data-field="sun">${ICON.sun}</span>
        <span class="ui-infocard__clock" data-field="clock">--:--</span>
      </div>
      <div class="ui-infocard__row ui-infocard__row--date">
        <span class="ui-infocard__date" data-field="date">Mon · Mar 1</span>
      </div>
      <div class="ui-infocard__row ui-infocard__row--season">
        <span class="ui-infocard__season" data-field="season">Year 1 · <span class="glyph glyph-inline">${ICON.spring}</span> Spring</span>
      </div>
      <div class="ui-infocard__divider"></div>
      <div class="ui-infocard__money-row">
        <div class="ui-chip ui-infocard__money" data-id="money">
          <span class="ui-chip__icon glyph">${ICON.coin}</span>
          <span class="ui-chip__value" data-field="money">0</span>
        </div>
        <button class="ui-infocard__shop" data-act="shop" title="Shop (truck)">
          <span class="ui-infocard__shop-icon glyph">${ICON.truck}</span>
          <span class="ui-infocard__shop-label">Shop</span>
        </button>
      </div>
      <div class="ui-infocard__energy" data-id="energy">
        <span class="ui-infocard__energy-icon glyph">⚡</span>
        <div class="ui-infocard__energy-track">
          <div class="ui-infocard__energy-fill" data-field="energy-fill"></div>
        </div>
        <span class="ui-infocard__energy-value" data-field="energy-value">0/0</span>
      </div>
      <div class="ui-infocard__plot" data-field="plot" hidden>
        <div class="ui-infocard__divider"></div>
        <div class="ui-infocard__plot-title" data-field="plot-title">${t('infocard.plot.title')}</div>
        <div class="ui-infocard__plot-row" data-field="plot-tiles">Tiles: 0</div>
        <div class="ui-infocard__plot-row" data-field="plot-weeds">Weeds: 0</div>
        <div class="ui-infocard__plot-row" data-field="plot-tilled">Tilled: 0%</div>
        <div class="ui-infocard__plot-row" data-field="plot-watered">Watered: 0%</div>
        <div class="ui-infocard__plot-row" data-field="plot-seeded">Seeded: 0%</div>
      </div>
    `;
    parent.appendChild(this.root);
    this.plotEl = this.root.querySelector('[data-field="plot"]') as HTMLDivElement;
    this.energyFill = this.root.querySelector('[data-field="energy-fill"]') as HTMLDivElement;
    this.energyValue = this.root.querySelector('[data-field="energy-value"]') as HTMLDivElement;
    this.root.addEventListener('click', this.onClick);
  }

  /** Late-binding hook for the panel manager so the Shop chip can
   *  open the Truck panel. Called from main.ts after both InfoCard
   *  and PanelManager exist. */
  connectPanels(panels: PanelManager) {
    this.panels = panels;
  }

  private onClick = (e: Event) => {
    const t = e.target as HTMLElement;
    const act = t.closest<HTMLElement>('[data-act]')?.dataset.act;
    if (act === 'shop' && this.panels) this.panels.toggle('truck');
  };

  update() {
    const sim = this.sim;
    const tod = sim.getTimeOfDay();
    const minutesOfDay = Math.floor(tod * 24 * 60);
    const hh = Math.floor(minutesOfDay / 60);
    const mm = minutesOfDay % 60;
    const clock = `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
    const sunIcon =
      tod < 0.22 || tod > 0.80 ? ICON.moon :
      tod < 0.28              ? ICON.dawn :
      tod < 0.72              ? ICON.sun :
                                  ICON.dusk;
    const sunKey =
      tod < 0.22 || tod > 0.80 ? 'moon' :
      tod < 0.28              ? 'dawn' :
      tod < 0.72              ? 'sun' :
                                  'dusk';
    const phase =
      tod < 0.22 || tod > 0.80 ? 'night' :
      tod < 0.28 || tod > 0.72 ? 'twilight' : 'day';
    const d = sim.getDate();
    const dateLabel = `${WEEKDAY_SHORT[d.weekday]} · ${MONTH_SHORT[d.month]} ${d.dayOfMonth}`;
    const seasonLabel = `Year ${d.year} · <span class="glyph glyph-inline">${SEASON_ICON[d.season]}</span> ${SEASON_LABEL[d.season]}`;
    const money = Math.floor(sim.state.res.money);
    // Re-paint only on hash change so the DOM is quiet between minute ticks.
    const hash = `${sunKey}|${clock}|${dateLabel}|${d.year}|${d.season}|${money}|${phase}`;
    if (hash !== this.lastHash) {
      this.lastHash = hash;
      this.root.dataset.phase = phase;
      this.setHtml('sun', sunIcon);
      this.set('clock', clock);
      this.set('date', dateLabel);
      this.setHtml('season', seasonLabel);
      this.set('money', `${money}`);
    }
    this.updatePlotSection();
    this.updateEnergy();
  }

  /** Drive the embedded energy bar — same color tiering as the legacy
   *  standalone EnergyBar (green > 50%, amber > 25%, red below). Width
   *  fills from left to right; the bar is horizontal here because the
   *  InfoCard's natural width gives plenty of room. */
  private updateEnergy() {
    const r = this.sim.state.res;
    const cur = Math.max(0, Math.floor(r.energy));
    const max = Math.max(1, r.maxEnergy);
    const frac = Math.max(0, Math.min(1, r.energy / max));
    const hash = `${cur}/${max}|${frac.toFixed(3)}`;
    if (hash === this.lastEnergyHash) return;
    this.lastEnergyHash = hash;
    this.energyFill.style.width = `${frac * 100}%`;
    this.energyFill.style.background =
      frac > 0.5 ? '#4ac440' :
      frac > 0.25 ? '#e8c040' : '#c04030';
    this.energyValue.textContent = `${cur}/${max}`;
  }

  /** Plot stats live in the StorageBar now — closer to the player's
   *  attention while they work the plot. Keep this section permanently
   *  hidden so the top-right card stays focused on time/money. */
  private updatePlotSection() {
    if (!this.plotEl.hidden) {
      this.plotEl.hidden = true;
      this.lastPlotHash = '';
    }
  }

  /** Screen target for FlyToHud — resolves any chip via document query. */
  target(id: string): { x: number; y: number } {
    const el = document.querySelector(`[data-id="${id}"]`) as HTMLElement | null;
    if (!el) return { x: window.innerWidth - 80, y: 30 };
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return { x: window.innerWidth - 80, y: 30 };
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  private set(field: string, v: string) {
    const el = this.root.querySelector(`[data-field="${field}"]`);
    if (el) el.textContent = v;
  }
  private setHtml(field: string, v: string) {
    const el = this.root.querySelector(`[data-field="${field}"]`);
    if (el) el.innerHTML = v;
  }
}
